import { ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Alert, Button, Col, Container, Dropdown, Form, Nav, Navbar, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import { format, isValid, parseISO } from "date-fns";
import { LuChevronDown, LuCircleUserRound } from "react-icons/lu";
import { GrGroup } from "react-icons/gr";
import bridgeIcon from "../asset/img/icon/bridge.png";
import tpeIcon from "../asset/img/icon/tpe.png";
import terminalIcon from "../asset/img/icon/terminal.png";
import baseIcon from "../asset/img/icon/base.png";
import pinpadIcon from "../asset/img/icon/pinpad.png";
import solutionIcon from "../asset/img/icon/solution.png";
import errorIcon from "../asset/img/icon/error.png";
import simIcon from "../asset/img/icon/sim.png";
import pieceIcon from "../asset/img/icon/piece.png";
import techIcon from "../asset/img/icon/tech.png";
import rapportIcon from "../asset/img/icon/rapport.png";
import adminIcon from "../asset/img/icon/admin.png";
import logoSylq from "../asset/img/sylq-white.png";
import lock from "../asset/img/icon/lock.png";
import profil from "../asset/img/icon/profil.png";
import deconnexion from "../asset/img/icon/deconnexion.png";
import mailIcon from "../asset/img/icon/mails.png";
import stockRI from "../asset/img/icon/stockRI.svg";
import "../asset/css/layout.css";
import { editUser, getGroup, updatPwdUser } from "../api/appliApi";
import { getVersionBack } from "../api/authApi";
import { AuthContext, useAuth } from "../context/AuthContext";
import { ConfirmModal } from "./ConfirmModal";
import { FormModal } from "./FormModal";
import { DisplayRoleProps, UserInfo } from "../utils/Type";

type NavItem = {
    key: string;
    to: string;
    label: string;
    icon: ReactNode;
    visible: boolean;
};

const clearFormData = () => ({
    id: "",
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    locale: "",
    active: true
});

export function formatDateTime(isoString?: string | null): string {
    if (!isoString) {
        return "N/A";
    }
    const date = parseISO(isoString);
    if (!isValid(date)) {
        return isoString;
    }
    return format(date, "dd/MM/yyyy HH:mm");
}

const Layout = () => {
    const auth = useContext(AuthContext);
    const { userName } = useAuth();
    const [show, setShow] = useState(false);
    const [modalShow, setModalShow] = useState(false);
    const [modalPWDShow, setModalPWDShow] = useState(false);
    const [modalAbout, setModalAbout] = useState(false);
    const role = auth?.role;
    const [userProfil, setUserProfil] = useState<UserInfo | null>();
    const [currentPassword, setCurrentPassword] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [groups, setGroups] = useState<{ id: string, name: string }[]>([]);
    const location = useLocation();
    const isActive = (path: string) => location.pathname === path;
    const version = process.env.REACT_APP_VERSION ?? "inconnue";
    const commit = process.env.REACT_APP_COMMIT_SHA ?? "n/a";
    const buildDate = process.env.REACT_APP_BUILD_DATE ?? "n/a";
    const [versionBack, setVersionBack] = useState("");
    const [buildBack, setBuildBack] = useState("");
    const [commitBack, setCommitBack] = useState("");
    const [isCompactMode, setIsCompactMode] = useState(() => window.innerWidth < 992);
    const [isCompactNavOpen, setIsCompactNavOpen] = useState(false);
    const [formData, setFormData] = useState(clearFormData());

    const roleName: { [key: string]: string } = {
        superadmin: "Super administrateur",
        admin: "Administrateur",
        technician: "Technicien"
    };

    const DisplayRole: React.FC<DisplayRoleProps> = ({ roleKey }) => {
        return <>{roleName[roleKey]}</>;
    };

    const getGroupeUser = useCallback(async () => {
        try {
            if (auth?.role === "superadmin") {
                setGroups([]);
            } else {
                const storedGroupes = localStorage.getItem("groupes");
                const groupesIds: number[] = storedGroupes ? JSON.parse(storedGroupes) : [];
                if (groupesIds.length === 0) {
                    setGroups([]);
                    return;
                }
                const data = await getGroup(groupesIds.map(id => id.toString()));
                setGroups(data.data.map((group: { id: string, name: string }) => ({ id: group.id, name: group.name })));
            }

        } catch (error) {
            setGroups([]);
        }
    }, [auth?.role]);

    useEffect(() => {
        getGroupeUser();
    }, [getGroupeUser]);

    useEffect(() => {
        if (userProfil) {
            setFormData({
                id: String(userProfil.id),
                email: userProfil.email,
                password: userProfil.password,
                firstName: userProfil.firstName,
                lastName: userProfil.lastName,
                locale: userProfil.locale,
                active: true
            });
        }
    }, [userProfil]);

    useEffect(() => {
        const fetchVersionBack = async () => {
            const data = await getVersionBack();
            setVersionBack(data.version);
            setBuildBack(data.buildDate);
            setCommitBack(data.commit);
        };

        fetchVersionBack();
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const compact = window.innerWidth < 992;
            setIsCompactMode(compact);
            if (!compact) {
                setIsCompactNavOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        setIsCompactNavOpen(false);
    }, [location.pathname]);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const handleLogOut = () => auth?.signOut();
    const notify = (message: string) => toast(message);
    const notifyError = (message: string) => toast.error(message);
    const handleClearForm = () => setFormData(clearFormData());

    const handleValidate = async () => {
        try {
            if (!userProfil) {
                return;
            }
            const emailChanged = userProfil.email !== formData.email;
            await editUser.put(formData);
            handleClearForm();
            notify("Enregistrer avec success !");
            setModalShow(false);
            if (!emailChanged) {
                window.location.reload();
            } else {
                handleLogOut();
            }
        } catch (error) {
            notifyError("Erreur lors de l'enregistrement.");
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleProfileShow = () => {
        setUserProfil(auth?.user.data);
        setModalShow(true);
    };

    const handlePWDShow = () => {
        setUserProfil(auth?.user.data);
        setModalPWDShow(true);
    };

    const handleAbout = () => {
        setModalAbout(true);
    };

    const navigationItems: NavItem[] = [
        {
            key: "bridge",
            to: "/bridge",
            label: "Passerelles",
            icon: <span className="nav-icon-h"><img src={bridgeIcon} alt="Passerelles" /></span>,
            visible: role === "superadmin" || role === "admin"
        },
        {
            key: "tpe",
            to: "/tpe",
            label: "Application TPEs",
            icon: <span className="nav-icon-v"><img src={tpeIcon} alt="Application TPE" /></span>,
            visible: role === "superadmin" || role === "admin"
        },
        {
            key: "device",
            to: "/device",
            label: "Terminaux",
            icon: <span className="nav-icon-h"><img src={terminalIcon} alt="Terminal" /></span>,
            visible: role === "superadmin" || role === "admin"
        },
        {
            key: "deviceBase",
            to: "/deviceBase",
            label: "Bases",
            icon: <span className="nav-icon-h"><img src={baseIcon} alt="Base" /></span>,
            visible: role === "superadmin" || role === "admin"
        },
        {
            key: "devicePinpad",
            to: "/devicePinpad",
            label: "Pinpads",
            icon: <span className="nav-icon-h"><img src={pinpadIcon} alt="Pinpad" /></span>,
            visible: role === "superadmin" || role === "admin"
        },
        {
            key: "solutionCode",
            to: "/solutionCode",
            label: "Solution Codes",
            icon: <span className="nav-icon-h"><img src={solutionIcon} alt="Solution code" /></span>,
            visible: role === "superadmin" || role === "admin"
        },
        {
            key: "errorCode",
            to: "/errorCode",
            label: "Error Codes",
            icon: <span className="nav-icon-h"><img src={errorIcon} alt="Error code" /></span>,
            visible: role === "superadmin" || role === "admin"
        },
        {
            key: "simType",
            to: "/simType",
            label: "Sim",
            icon: <span className="nav-icon-h"><img src={simIcon} alt="Sim" /></span>,
            visible: role === "superadmin" || role === "admin"
        },
        {
            key: "serialPartCode",
            to: "/serialPartCode",
            label: "Pièces détachées",
            icon: <span className="nav-icon-h"><img src={pieceIcon} alt="Pièce détachée" /></span>,
            visible: role === "superadmin" || role === "admin"
        },
        {
            key: "user",
            to: "/user",
            label: role === "superadmin" ? "Utilisateurs" : "Techniciens",
            icon: <span className="nav-icon-h"><img src={techIcon} alt="Technicien" /></span>,
            visible: role === "superadmin" || role === "admin"
        },
        {
            key: "report",
            to: "/report",
            label: "Rapports",
            icon: <span className="nav-icon-v"><img src={rapportIcon} alt="Rapport" /></span>,
            visible: true
        },
        {
            key: "groupRI",
            to: "/groupRI",
            label: "Stockage RI",
            icon: <span className="nav-icon-h"><img src={stockRI} alt="Stockage RI" /></span>,
            visible: role === "superadmin"
        },
        {
            key: "mail",
            to: "/mail",
            label: "Gestion mail",
            icon: <span className="nav-icon-h"><img src={mailIcon} alt="Gestion mail" /></span>,
            visible: role === "superadmin"
        },
        {
            key: "group",
            to: "/group",
            label: "Groupe users",
            icon: <span className="nav-icon-h"><GrGroup size={16} color="white" /></span>,
            visible: role === "superadmin"
        },
        {
            key: "adminMessage",
            to: "/adminMessage",
            label: "Administration",
            icon: <span className="nav-icon-h"><img src={adminIcon} alt="Administration" /></span>,
            visible: role === "admin"
        }
    ];

    const renderNavigation = (className?: string) => (
        <Nav className={className ?? "flex-column sidebar-nav"}>
            {navigationItems.filter((item) => item.visible).map((item) => (
                <Nav.Link
                    key={item.key}
                    as={Link}
                    to={item.to}
                    className={isActive(item.to) ? "active" : ""}
                >
                    {item.icon}
                    {item.label}
                </Nav.Link>
            ))}
        </Nav>
    );

    const handleSubmitPwd = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            setError("Les mots de passe ne correspondent pas.");
        } else {
            try {
                const response = await updatPwdUser.post(password, currentPassword);
                if (response?.status === 204) {
                    setError("Mot de passe incorrect.");
                } else {
                    setModalPWDShow(false);
                    notify("Mot de passe modifier avec succès !.");
                    auth?.signOut();
                }
            } catch (responseError) {
                setModalPWDShow(false);
                notifyError("Erreur lors de l'enregistrement.\n" + responseError);
            }
        }
    };

    return (
        <>
            <div className="d-flex flex-column vh-100">
                <Navbar className="custom-navbar" variant="dark" expand="lg">
                    <Container fluid>
                        <Navbar.Brand className="custom-brand">
                            <img src={logoSylq} width={80} alt="logo sylq" />
                            <i className="text-white ms-2">Technicien</i>
                        </Navbar.Brand>
                        {isCompactMode && (
                            <Button
                                variant="light"
                                className="layout-compact-toggle"
                                onClick={() => setIsCompactNavOpen((open) => !open)}
                            >
                                {isCompactNavOpen ? "Fermer" : "Menu"}
                            </Button>
                        )}
                        <div className="layout-navbar-meta d-flex align-items-center ms-auto me-3 gap-3">
                            {role && <span className="custom-info-navbar fw-bold text-white"><DisplayRole roleKey={role} /></span>}
                            {groups.map(g => (
                                <span key={g.id} className="custom-info-group-navbar">{g.name}</span>
                            ))}
                            <span className="text-white">{userName}</span>
                            <Dropdown align="end">
                                <Dropdown.Toggle id="dropdown-basic" className="dropdown-user-icon" variant="link">
                                    <div className="d-flex align-items-center gap-1">
                                        <LuCircleUserRound size={34} color="white" />
                                        <LuChevronDown size={20} color="white" />
                                    </div>
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="custom-dropdown-menu">
                                    <Dropdown.Item onClick={handleProfileShow} className="d-flex justify-content-between align-items-center">Profil <img src={profil} width={16} alt="Profil" /></Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={handlePWDShow} className="d-flex justify-content-between align-items-center">Modifier votre mot de passe <img src={lock} width={16} alt="Modifier mot de passe" /></Dropdown.Item>
                                    <Dropdown.Divider />
                                    {(role === "superadmin") && (
                                        <>
                                            <Dropdown.Item onClick={handleAbout} className="d-flex justify-content-between align-items-center">À propos <img src={lock} width={16} alt="À propos" /></Dropdown.Item>
                                            <Dropdown.Divider />
                                        </>
                                    )}
                                    <Dropdown.Item onClick={handleShow} className="d-flex justify-content-between align-items-center">Se déconnecter <img src={deconnexion} width={16} alt="Se déconnecter" /></Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </Container>
                </Navbar>

                {isCompactMode && isCompactNavOpen && (
                    <div className="compact-nav-panel sidebar-shadow">
                        {renderNavigation("sidebar-nav compact-sidebar-nav")}
                    </div>
                )}

                <Container fluid className="flex-grow-1">
                    <Row className="h-100">
                        {!isCompactMode && (
                            <Col
                                className="p-0 sidebar-shadow layout-sidebar"
                                style={{ flex: "0 0 240px", maxWidth: "240px", width: "240px" }}
                            >
                                {renderNavigation()}
                            </Col>
                        )}

                        <Col
                            className={isCompactMode ? "p-3 p-md-4" : "p-4"}
                            style={{ flex: "1 1 0%", minWidth: 0, backgroundColor: "#F6F8FD", gap: "20px" }}
                        >
                            <Outlet />
                        </Col>
                    </Row>
                </Container>
            </div>

            <ConfirmModal
                show={show}
                onValidate={handleLogOut}
                onAbort={handleClose}
                body={"Êtes-vous sûr de vouloir vous déconnecter ?"}
                isNotif={true}
            />

            <FormModal
                show={modalShow}
                onValidate={handleValidate}
                onAbort={() => setModalShow(false)}
                title="Votre profile"
                body={
                    <>
                        <Form>
                            <Form.Group className="mb-3" controlId="profile.ControlInput">
                                <Form.Control
                                    type="text"
                                    placeholder="Nom"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className="uppercase-input"
                                    autoFocus
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Control
                                    type="text"
                                    placeholder="Prénom"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Control
                                    type="text"
                                    placeholder="Adresse email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Control
                                    type="text"
                                    placeholder="Locale"
                                    name="locale"
                                    value={formData.locale}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Form>
                    </>
                }
            />

            <FormModal
                show={modalPWDShow}
                footer={<></>}
                onValidate={() => { }}
                onAbort={() => setModalPWDShow(false)}
                title="Modifier votre mot de passe"
                body={
                    <>
                        {error && <Alert variant="danger">{error}</Alert>}
                        <Form onSubmit={handleSubmitPwd}>
                            <Form.Group className="mb-3">
                                <Form.Control
                                    type="password"
                                    placeholder="Mot de passe actuel"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Control
                                    type="password"
                                    placeholder="Nouveau mot de passe"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Control
                                    type="password"
                                    placeholder="Confirmez le mot de passe"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                />
                            </Form.Group>
                            <Button variant="primary" type="submit" className="w-100">
                                Modifier le mot de passe
                            </Button>
                        </Form>
                    </>
                }
            />

            <FormModal
                show={modalAbout}
                onValidate={() => setModalAbout(false)}
                onAbort={() => setModalAbout(false)}
                title="À propos"
                showFooter={false}
                body={
                    <>
                        <div style={{ padding: "2rem" }}>
                            <h3>Version interface d’administration(Front Back-Office)</h3>
                            <div><b>Version:</b> {version}</div>
                            <div><b>Commit:</b> {commit}</div>
                            <div><b>Build date:</b> {formatDateTime(buildDate)}</div>
                        </div>
                        <div style={{ padding: "2rem" }}>
                            <h3>Version moteur Applicatif(Back-End Technique)</h3>
                            <div><b>Version:</b> {versionBack}</div>
                            <div><b>Commit:</b> {commitBack}</div>
                            <div><b>Build date:</b> {formatDateTime(buildBack)}</div>
                        </div>
                    </>
                }
            />
        </>
    );
};

export default Layout;
